import { type HTMLAttributes } from 'react';
import { Check, X, Eye, AlertTriangle, type LucideIcon } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../utils';

/**
 * Action types for governance rules
 */
export type ActionType = 'allow' | 'deny' | 'escalate' | 'obfuscate' | 'audit';

/**
 * ActionBadge Variants
 *
 * Premium styling for governance rule action indicators.
 * Uses the Agion brand color palette:
 * - allow: Emerald (permitted action)
 * - deny: Rose (blocked action)
 * - escalate: Amber (needs review)
 * - obfuscate: Violet (data transformation)
 */
export const actionBadgeVariants = cva(
  [
    'inline-flex items-center gap-1.5 font-medium',
    'border rounded-md',
    'transition-colors',
  ],
  {
    variants: {
      action: {
        allow: [
          'bg-emerald-50 text-emerald-800 border-emerald-200/70',
          'dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800/50',
        ],
        deny: [
          'bg-rose-50 text-rose-800 border-rose-200/70',
          'dark:bg-rose-950/50 dark:text-rose-300 dark:border-rose-800/50',
        ],
        escalate: [
          'bg-amber-50 text-amber-800 border-amber-200/70',
          'dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800/50',
        ],
        obfuscate: [
          'bg-violet-50 text-violet-800 border-violet-200/70',
          'dark:bg-violet-950/50 dark:text-violet-300 dark:border-violet-800/50',
        ],
        audit: [
          'bg-slate-50 text-slate-800 border-slate-200/70',
          'dark:bg-slate-950/50 dark:text-slate-300 dark:border-slate-800/50',
        ],
      },
      size: {
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-2.5 py-0.5 text-xs',
        lg: 'px-3 py-1 text-sm',
      },
    },
    defaultVariants: {
      action: 'allow',
      size: 'md',
    },
  }
);

export type ActionBadgeVariants = VariantProps<typeof actionBadgeVariants>;

// Icon mapping for each action type
const actionIcons: Record<ActionType, LucideIcon> = {
  allow: Check,
  deny: X,
  escalate: AlertTriangle,
  obfuscate: Eye,
  audit: Eye,
};

// Default labels for each action type
const actionLabels: Record<ActionType, string> = {
  allow: 'Allow',
  deny: 'Deny',
  escalate: 'Escalate',
  obfuscate: 'Obfuscate',
  audit: 'Audit',
};

// Icon size classes
const iconSizes = {
  sm: 'h-3 w-3',
  md: 'h-3.5 w-3.5',
  lg: 'h-4 w-4',
};

export interface ActionBadgeProps
  extends Omit<HTMLAttributes<HTMLSpanElement>, 'action'>,
    ActionBadgeVariants {
  /** The action type to display */
  action: ActionType;
  /** Whether to show the action icon */
  showIcon?: boolean;
  /** Custom label to display (if not provided, action name is used) */
  label?: string;
}

/**
 * ActionBadge - Unified rule action indicator component
 *
 * Displays governance rule actions with consistent premium styling.
 * Uses the Agion brand color palette.
 *
 * Action types:
 * - allow: Permitted action (emerald)
 * - deny: Blocked action (rose)
 * - escalate: Needs review (amber)
 * - obfuscate: Data transformation (violet)
 */
export function ActionBadge({
  className,
  action,
  size = 'md',
  showIcon = true,
  label,
  ...props
}: ActionBadgeProps) {
  const Icon = actionIcons[action] || actionIcons.allow;
  const displayLabel = label || actionLabels[action];
  const iconSize = iconSizes[size || 'md'];

  return (
    <span
      className={cn(actionBadgeVariants({ action, size }), className)}
      {...props}
    >
      {showIcon && <Icon className={cn(iconSize, 'flex-shrink-0')} aria-hidden="true" />}
      <span>{displayLabel}</span>
    </span>
  );
}

/**
 * Get the default label for an action type
 */
export function getActionLabel(action: ActionType): string {
  return actionLabels[action];
}

/**
 * Map action type to Badge variant for compatibility
 */
export function getActionBadgeVariant(action: ActionType): 'success' | 'error' | 'warning' | 'purple' {
  switch (action) {
    case 'allow':
      return 'success';
    case 'deny':
      return 'error';
    case 'escalate':
      return 'warning';
    case 'obfuscate':
      return 'purple';
    case 'audit':
      return 'success';
    default:
      return 'success';
  }
}
